import { useState } from "react";
import { toast } from "sonner";
import { useDebounce } from "@/shared/hooks/useDebounce";
import {
  useBankMovementFusionCandidatesQuery,
  useFuseBankMovement,
  useSplitBankMovement,
} from "../../hooks/useAccountingQueries";
import { extractApiErrorMessage } from "./utils";
import type { BankMovement } from "../../types/accounting.types";

type UseFusionParams = {
  bankMovementId: string
  open: boolean
  bankMovement: BankMovement | null
  /** Charger les candidats : la fusion ne dépend pas du flag Bridge, seulement du type de mouvement. */
  enabled: boolean
  onFuseOrSplitComplete: () => void
};

export const useFusion = ({
  bankMovementId,
  open,
  bankMovement,
  enabled,
  onFuseOrSplitComplete,
}: UseFusionParams) => {
  const [fusionSearch, setFusionSearch] = useState("");
  const { debouncedValue: debouncedFusionSearch } = useDebounce(fusionSearch, 300);
  const [fusingCandidateId, setFusingCandidateId] = useState<string | null>(null);

  const {
    data: fusionCandidates = [],
    isLoading: isFusionCandidatesLoading,
    refetch: refetchFusionCandidates,
  } = useBankMovementFusionCandidatesQuery(
    { bankMovementId, smartQuery: debouncedFusionSearch },
    open && Boolean(bankMovementId) && !bankMovement?.dateFusion && enabled,
  );

  const fuseMutation = useFuseBankMovement(bankMovementId);
  const splitMutation = useSplitBankMovement(bankMovementId);

  const isFusionBusy = fuseMutation.isPending;
  const isSplitBusy = splitMutation.isPending;

  const handleFuse = async (candidateId: string) => {
    setFusingCandidateId(candidateId);
    try {
      await fuseMutation.mutateAsync(candidateId);
      onFuseOrSplitComplete();
      refetchFusionCandidates().catch(() => {});
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error));
    }
    finally {
      setFusingCandidateId(null);
    }
  };

  const handleSplit = async () => {
    try {
      await splitMutation.mutateAsync();
      onFuseOrSplitComplete();
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error));
    }
  };

  const resetFusion = () => {
    setFusionSearch("");
  };

  return {
    fusionSearch,
    setFusionSearch,
    fusionCandidates,
    isFusionCandidatesLoading,
    isFusionBusy,
    isSplitBusy,
    fusingCandidateId,
    handleFuse: (candidateId: string) => void handleFuse(candidateId),
    handleSplit: () => void handleSplit(),
    resetFusion,
  };
};
